import TableRow from "src/components/Shared/Table/TableRow";
import UserProfile from "src/components/Users/UserProfile";
import { apiRequest } from "src/async/apiUtils";
import { useState, useEffect } from "react";
import { ArrowPathIcon, KeyIcon, StarIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { classNames } from "src/helpers/classNames";
import { previousDateInString } from "src/helpers/dateHelper";
import moment from "moment";

export default function ClientUserEntry({ user, client, refreshList }) {
  const [loading, setLoading] = useState(false);
  const [tooltipContent, setTooltipContent] = useState("");

  useEffect(() => {
    if (user?.default_user) {
      setTooltipContent("This user is already a Super Admin");
    } else if (loading) {
      setTooltipContent("Processing...");
    } else {
      setTooltipContent("Promote to Super Admin");
    }
  }, [loading, user?.default_user]);

  const handleMakeSuperAdmin = async () => {
    if (!client?._id || !user?._id) return;
    try {
      setLoading(true);
      const res = await apiRequest("put", `/clients/:client_id/users/:user_id/default_user`, {
        params: { client_id: client._id, user_id: user?._id },
      });
      if (res?.data?.status === 200) {
        refreshList();
        const user_name = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
        toast.success(`${user_name} is now Super Admin`);
      }
    } catch (error) {
      toast.error("Error making user super admin");
    } finally {
      setLoading(false);
    }
  };

  return (
    <TableRow keyId={user?._id}>
      <td className="break-word block w-full whitespace-pre-wrap px-6 py-0 pt-3 text-sm text-gray-500 sm:table-cell sm:w-auto sm:py-3.5">
        <div className="flex items-center justify-start gap-2">
          <div className="h-10 w-10 overflow-hidden rounded-full">
            <UserProfile
              user={{
                ...user,
                image: `/api/clients/${client?._id}/users/${user?._id}/image`,
              }}
              className="!bg-highlightColor !text-white"
            />
          </div>
          <div className="text-black">
            <div className="flex gap-x-2">
              {`${user?.first_name || ""} ${user?.last_name || ""}`.trim()}
              {user?.type === "admin" && !user?.default_user && (
                <div className="flex h-5 w-5 items-center justify-center rounded bg-amber-100 text-sm font-semibold transition-all duration-200 group-hover:bg-white">
                  <KeyIcon className="h-3 w-3 stroke-2 text-amber-500" />
                </div>
              )}
              {user?.default_user && (
                <div
                  className="flex h-5 w-5 items-center justify-center gap-x-1 rounded bg-purple-100 px-1 py-0.5 text-xs font-medium text-purple-600"
                  title="Super Admin">
                  <StarIcon className="h-3 w-3 stroke-2" />
                </div>
              )}
            </div>

            <div className="text-xs text-gray-500">{user?.email}</div>
          </div>
        </div>
      </td>

      <td className="block w-full whitespace-pre-wrap break-all px-3 py-1 text-start text-xs text-gray-600 sm:table-cell sm:w-auto sm:py-3.5">
        {!user.last_activity_time ? (
          "Never logged in"
        ) : (
          <>
            {moment(user.last_activity_time).format("DD MMM YYYY, hh:mm A")}
            {" — "}
            {previousDateInString(user.last_activity_time)}
          </>
        )}
      </td>

      <td className="px-6 py-3 text-center">
        <button
          disabled={loading || user?.default_user}
          data-tooltip-id={`tooltip-${user?._id}`}
          data-tooltip-content={tooltipContent}
          onClick={handleMakeSuperAdmin}
          className="ml-auto flex items-center  justify-end gap-x-2">
          {!user?.default_user && <ArrowPathIcon className={classNames("h-4 w-4  text-highlightColor", loading ? "animate-spin" : "")} />}
          <div className={`relative flex items-center justify-center rounded-xl border border-highlightColor bg-gray-100 p-0.5 text-highlightColor transition hover:bg-gray-200 ${user?.default_user ? "cursor-not-allowed" : ""}`}>
            {user?.type === "admin" && <span className="rounded-full  px-2 py-[1px] text-xs">{user?.default_user && <span>Super </span>}Admin</span>}
          </div>
        </button>

        <Tooltip
          id={`tooltip-${user?._id}`}
          place="top"
          style={{
            backgroundColor: "#1f2937",
            color: "#fff",
            fontSize: "0.75rem",
            borderRadius: "6px",
            padding: "5px 8px",
          }}
        />
      </td>
    </TableRow>
  );
}
